'use client'

/**
 * Custom hook to manage wallet connection and authentication state
 * Manages:
 * - Wallet connection and disconnection
 * - Account state and chain information
 * - Authentication flow
 * - Analytics tracking
 * - MetaMask event handling
 */

// React core imports
import { useEffect } from 'react'
import { useRouter } from 'next/navigation'

// External libraries and utilities
import { useLogout, usePrivy } from '@privy-io/react-auth'
import { useChains, useDisconnect } from 'wagmi'
import { sendGTMEvent } from '@next/third-parties/google'
import { useChain } from '@azuro-org/sdk'
import {
  useAAWalletClient,
  useAccount,
} from '@azuro-org/sdk-social-aa-connector'

// Internal application modules
import { ConnectorName } from 'wallet'
import { useUserContext } from 'src/contexts/user'
import { constants } from 'helpers'

export const useWallet = () => {
  // Navigation and routing
  const route = useRouter()

  // Authentication state
  const { authenticated, ready, user } = usePrivy()
  const { logoutUser, currentUser } = useUserContext()
  const { logout } = useLogout()
  const { disconnect } = useDisconnect()
  const { setAppChainId } = useChain()

  // Account and wallet state
  const {
    address,
    connector,
    isConnected,
    isConnecting,
    isReconnecting,
    chain,
    chainId,
    isAAWallet,
  } = useAccount()

  /**
   * Handles complete logout process including:
   * - Analytics tracking
   * - User state cleanup
   * - Wallet disconnection
   * - Chain reset
   * - Local storage cleanup
   */
  const logoutApp = async () => {
    sendGTMEvent({
      event: 'logout_completed',
      user_id: currentUser?.id,
      login_toolkit: 'privy',
      wallet_address: user ? user?.smartWallet?.address : address,
    })
    logoutUser()
    logout()
    disconnect()
    setAppChainId(137)
    localStorage.removeItem(constants.localStorageKeys.hasFetchedUserInfo)
    route.refresh()
  }

  // Chain and wallet client setup
  const chains = useChains()
  const aaWalletClient = useAAWalletClient()

  // Wallet connection type check
  const isWalletConnect = connector?.name === ConnectorName.WalletConnect

  /**
   * Handle MetaMask account change events
   * Automatically logs out user if all accounts are disconnected
   */
  useEffect(() => {
    const handleAccountsChanged = (accounts: string[]) => {
      if (accounts.length === 0 && !isAAWallet) {
        logoutApp()
      }
    }

    if (typeof window !== 'undefined' && window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged)
      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged)
      }
    }
  }, [logoutApp, isAAWallet])

  return {
    // Account information
    account: (isAAWallet
      ? user?.smartWallet?.address
      : user?.wallet?.address) as `0x${string}`,
    connector,
    aaWalletClient,

    // Chain information
    chain,
    chains,
    chainId,

    // Connection states
    isConnected,
    isConnecting,
    isReconnecting: isReconnecting || (authenticated && !address),
    isWalletConnect,
    isAAWallet,
    isReady: Boolean(address && !Boolean(user?.id)) || ready,

    // Actions
    logoutApp,
  }
}
